import { ServerType } from '@Types/CreateNewSystemConnectionRequest';
import {
	FieldTroubleshooting,
	SensorTroubleshooting,
	TestableFields,
} from '@Types/peek';
import { SystemConnection } from '@Types/systemConnections.types';
import { useGlobalization } from '@innovyze/stylovyze';
import { useMemo } from 'react';

type Messages = Record<TestableFields, string>;
export type SensorTbltMsgsReturn = {
	messages: Partial<Messages>;
	isTimeFramed: boolean;
};

/**
 * Set priority based on dependencies where if we get a error message with priority, dependencies will be skipped
 * Example:
 * error in tableName, will skip automatically idField, channelField, dateField, and tagValue since is a dependency of idField
 */
const MESSAGES_PRIORITIES: Map<TestableFields, string[]> = new Map([
	['dataSource', ['tableName']],
	['tableName', ['idField', 'channelField', 'dateField']],
	['idField', ['tagValue']],
	['channelField', []],
	['dateField', []],
	['tagValue', []],
]);

const getErrorsByPriority = (
	sensorTroubleshooting: Partial<SensorTroubleshooting>,
): Record<string, string> => {
	const sensorTroubleshootingNew: Record<string, string> = {};
	let ignoreFields: string[] = [];
	for (const errorMessage in sensorTroubleshooting) {
		const dependencies = MESSAGES_PRIORITIES.get(
			errorMessage as TestableFields,
		);
		if (dependencies?.length) {
			ignoreFields = [...ignoreFields, ...dependencies];
		}
	}

	const setIgnoreFields = new Set(ignoreFields);
	for (const [key] of MESSAGES_PRIORITIES) {
		const error = sensorTroubleshooting[key];
		if (!error || setIgnoreFields.has(key)) continue;

		sensorTroubleshootingNew[key] = error;
	}
	return sensorTroubleshootingNew;
};

export const useSensorTroubleshootMessages = (
	sensorTroubleshooting: Partial<SensorTroubleshooting>,
	dataSourceId: string,
	systemConnections: SystemConnection[],
): SensorTbltMsgsReturn => {
	const { t } = useGlobalization();

	const isTimeFramed = useMemo(() => {
		const TimeFramedSystems: ServerType[] = ['PIProvider', 'PI'];
		const currentDs = systemConnections.find(
			sc => sc.SystemConnectionId === dataSourceId,
		);
		return TimeFramedSystems.some(e => e === currentDs?.serverType);
	}, [dataSourceId, systemConnections]);

	const MESSAGES_BASE = useMemo<
		Record<TestableFields, { error: string; warning: string }>
	>(
		() => ({
			dataSource: {
				error: t('Error: Cannot use current data source'),
				warning: t('Warning: Cannot use current data source'),
			},
			channelField: {
				error: t('Error: Cannot use current channel field value'),
				warning: t('Warning: Cannot use current channel field value'),
			},
			dateField: {
				error: t('Error: Cannot use current date field value'),
				warning: t('Warning: Cannot use current date field value'),
			},
			idField: {
				error: t('Error: Cannot use current id field value'),
				warning: t('Warning: Cannot use current id field value'),
			},
			tableName: {
				error: t('Error: Cannot use current table name'),
				warning: t('Warning: Cannot use current table name'),
			},
			tagValue: {
				error: t('Error: Cannot use current tag value'),
				warning: t(
					'Warning: Cannot find data within the last 24 hours',
				),
			},
		}),
		[],
	);

	const msgsMap = useMemo(() => {
		if (!sensorTroubleshooting?.state) return {} as Messages;

		const errors = Object.entries<FieldTroubleshooting>(
			getErrorsByPriority(sensorTroubleshooting),
		);

		return errors.reduce((acc, cur) => {
			const [field, code] = cur;
			const key = field as TestableFields;
			const hasError = !!code;
			const accessor = isTimeFramed ? 'warning' : 'error';
			if (hasError) acc[key] = MESSAGES_BASE[key][accessor];
			return acc;
		}, {} as Messages);
	}, [sensorTroubleshooting?.state]);

	return { messages: msgsMap, isTimeFramed };
};

export default useSensorTroubleshootMessages;
